import { z } from 'zod';
import { FormInstanceStatus } from './form-instance-status.enum';
import { FormCategory } from '../enums/form-category.enum';
import { formDefinition } from './form-definition.type';

export const formInstanceNotification = z.object({
  name: z.string().min(1),
  email: z.array(
    // Custom e-mail regex, because the native zod email() rejects many valid addresses,
    // e. g. such that contain umlauts.
    z.string().regex(new RegExp('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$')).meta({ examples: ['some.customer@newroom-media.de'] }),
  ),
});
export type FormInstanceNotification = z.infer<typeof formInstanceNotification>;

export type FormInstanceValues = { [key: string]: string | FormInstanceValues | undefined };
export const formInstanceValues: z.ZodType<FormInstanceValues> = z.record(
  // Key must always be a non-empty string.
  z.string().min(1),
  // Value can be a value (string, can be empty)
  z.string()
    // OR another nested object
    .or(
      z.lazy(() => formInstanceValues),
    ),
);

export const formInstance = z.object({
  id: z.int().positive().readonly().optional().meta({ maximum: undefined }),
  formsDefinitionsId: z.int().positive().readonly().meta({ maximum: undefined }),
  orgaentitiesPath: z.string().min(3).startsWith('/').endsWith('/'),
  category: z.enum(FormCategory),
  name: z.string().trim().min(3),
  title: z.string().trim().min(3),
  referenceDate: z.iso.datetime().meta({ pattern: undefined }),
  status: z.enum(FormInstanceStatus),
  language: z.string().min(2).max(2),
  country: z.string().min(2).max(2),
  market: z.string().min(1),
  creationDate: z.iso.datetime().meta({ pattern: undefined }),
  expirationDate: z.iso.datetime().meta({ pattern: undefined }),
  updateDate: z.iso.datetime().meta({ pattern: undefined }).nullable(),
  archiveDate: z.iso.datetime().meta({ pattern: undefined }).nullable().meta({ description: 'Indicates when the form instance was archived' }),
  notifications: z.array(formInstanceNotification)
    .meta({
      examples: [
        [
          {
            email: ['didar.alam+MADRegion@newroom-media.de'],
            name: 'Region',
          },
          {
            email: ['didar.alam+MADMarket@newroom-media.de'],
            name: 'Market',
          },
        ],
      ],
    }),
  // TODO: Not defined and not implemented yet.
  updateHistory: z.array(z.object({})),
  values: formInstanceValues
    .meta({
      description: 'Form input values in nested structure',
      type: 'object',
      examples: [
        {
          dealerOrAgentProfitabilityDevelopmentForecast: {
            dealerOrAgentRoSTotal: {
              'baseline2019': '2.8%',
              '2021': '3.5%',
              '2022': '16.1%',
              '2023YTDQ1': '',
              '2023Forecast': '5%',
              '2024Forecast': '4.4%',
            },
            dealerOrAgentRoSSales: {
              'baseline2019': '',
              '2021': '',
              '2022': '',
              '2023YTDQ1': '',
              '2023Forecast': '',
              '2024Forecast': '',
            },
            comment: 'Are those numbers really correct?',
          },
        },
      ],
    }),
  additionalAttributes: z.record(z.string(), z.unknown()),
  lockId: z.string().length(31).nullable()
    .meta({
      description: 'Lock ID in format `[timestamp]-[random numbers]` (`2024-02-06T15:23:12.000Z-968455`)',
      examples: ['2024-02-06T15:23:12.000Z-968455'],
    }),
  version: z.int().positive().meta({ maximum: undefined }),
});
export const formInstanceTransformed = formInstance
  .transform((val) => {
    // Lock IDs should timeout (return `null`) after 2 hours.
    if (val.lockId) {
      const lockIdTimestamp = val.lockId.substring(0, 24);
      const twoHoursAgo = Date.now() - 7_200_000;
      if (new Date(lockIdTimestamp).getTime() < twoHoursAgo) {
        val.lockId = null;
      }
    }

    return val;
  });
export const formInstances = z.array(formInstance);

export type FormInstance = z.infer<typeof formInstance>;

export const partialFormInstance = formInstance.partial();
export type PartialFormInstance = z.infer<typeof partialFormInstance>;

export const formInstanceWithDefinition = z.intersection(
  formInstance,
  z.object({
    formDefinition: z.optional(formDefinition),
  }),
);

export type FormInstanceWithDefinition = z.infer<typeof formInstanceWithDefinition>;

export const formInstanceUpdateInput = formInstance.pick({
  status: true,
  expirationDate: true,
  values: true,
  additionalAttributes: true,
}).strict().partial();

export type FormInstanceUpdateInput = z.infer<typeof formInstanceUpdateInput>;

type CamelToSnakeCase<S extends string> =
  S extends `${infer T}${infer U}` ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}` : S;

export type FormInstanceDatabase = { [K in keyof FormInstance as CamelToSnakeCase<K>]: FormInstance[K] };
